import React, { useEffect, useRef, useState } from "react";
import { Activity, AlertTriangle, Wifi, WifiOff } from "lucide-react";
import api from "../../lib/api";

/**
 * Lightweight deploy-heartbeat badge for the dashboard footer.
 *
 * Polls GET /api/health every 60s and surfaces:
 *  - status (ok / degraded / down)
 *  - uptime (h m)
 *  - db ping latency (ms)
 *  - build SHA / env
 *
 * A pulsing dot animates on every successful tick so the operator can see
 * "the deployment is breathing".
 */
function formatUptime(seconds) {
  if (!seconds || seconds < 0) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${seconds % 60}s`;
}

export default function DeployHeartbeat() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [pulse, setPulse] = useState(0);
  const timer = useRef(null);

  async function tick() {
    try {
      const r = await api.get("/health");
      setData(r.data);
      setError(false);
      setPulse((p) => p + 1);
    } catch {
      setError(true);
    }
  }

  useEffect(() => {
    tick();
    timer.current = setInterval(tick, 60000);
    return () => clearInterval(timer.current);
  }, []);

  const offline = error || !data;
  const degraded = data && (data.status === "degraded" || data.db_ok === false);
  const tone =
    offline ? "border-red-500/60 bg-red-500/10 text-red-200"
    : degraded ? "border-amber-500/60 bg-amber-500/10 text-amber-200"
    : "border-emerald-500/60 bg-emerald-500/10 text-emerald-200";
  const dotTone =
    offline ? "bg-red-400"
    : degraded ? "bg-amber-400"
    : "bg-emerald-400";

  const Icon = offline ? WifiOff : degraded ? AlertTriangle : Activity;

  return (
    <div
      data-testid="deploy-heartbeat"
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 font-mono text-[11px] backdrop-blur-sm transition-colors ${tone}`}
      title={
        offline ? "Backend unreachable"
        : `Last check ${new Date(data?.timestamp || Date.now()).toLocaleTimeString()}`
      }
    >
      <span className="relative inline-flex h-2 w-2">
        <span
          key={pulse}
          className={`absolute inset-0 inline-flex h-full w-full animate-ping rounded-full opacity-75 ${dotTone}`}
        />
        <span className={`relative inline-flex h-2 w-2 rounded-full ${dotTone}`} />
      </span>
      <Icon className="h-3 w-3" />
      {offline ? (
        <span>
          <b>OFFLINE</b> · backend not responding
        </span>
      ) : (
        <>
          <span>
            <b>{data.status?.toUpperCase()}</b>
          </span>
          <span className="opacity-70">·</span>
          <span>
            uptime <b>{formatUptime(data.uptime_seconds)}</b>
          </span>
          <span className="opacity-70">·</span>
          <span>
            db <b>{data.db_ping_ms != null ? `${data.db_ping_ms} ms` : "—"}</b>
          </span>
          <span className="opacity-70">·</span>
          <span>
            build <b className="text-cyan-300">{(data.build_sha || "dev").slice(0, 7)}</b>
          </span>
          {data.env && data.env !== "production" && (
            <>
              <span className="opacity-70">·</span>
              <span className="rounded bg-cyan-500/20 px-1 text-[10px] font-bold uppercase text-cyan-200">
                {data.env}
              </span>
            </>
          )}
        </>
      )}
    </div>
  );
}
